<template>
    <ag-grid-vue
        class="ag-theme-custom"
        :style="gridStyle"
        :class="gridClass"
        :row-data="rowData"
        :column-defs="columnDefs"
        :grid-options="gridOptionsExtended"
        :default-col-def="defaultColDefExtended"
        :edit-type="editType"
        :single-click-edit="singleClickEdit"
        group-headers
        auto-params-refresh
        :dom-layout="domLayout"
        @grid-ready="onReady"
        @cell-value-changed="onCellValueChanged"
        @cell-key-down="onCellKeyDown"
        @row-data-updated="onRowDataUpdated"
        @model-updated="onModelUpdated"
    />
</template>

<script>
// There are two ways of importing and using the AgGrid library - packages and modules.
// Modules are recommended as they allow developers to opt-in to install and enable features of AgGrid explicitly,
// leading to a more optimised bundle size. For more info, see: https://www.ag-grid.com/documentation/javascript/modules/

// ToDo: For vue 3 upgrade packages were used need as modules provided errors of version compatibility
import { markRaw, toRaw } from 'vue';
import { AgGridVue } from 'ag-grid-vue3';
import { merge, debounce } from 'lodash';

export default {
    name: 'PromoAgGrid',
    components: {
        'ag-grid-vue': AgGridVue,
    },
    props: {
        rowData: {
            required: true,
            type: Array,
            default: () => [],
        },
        columnDefs: {
            required: true,
            type: Array,
            default: () => [],
        },
        gridClass: {
            required: false,
            type: String,
            default: '',
        },
        gridStyle: {
            required: false,
            type: String,
            default: '',
        },
        gridOptions: {
            required: false,
            type: Object,
        },
        defaultColDef: {
            required: false,
            type: Object,
        },
        editType: {
            required: false,
            type: String,
        },
        domLayout: {
            required: false,
            type: String,
        },
        singleClickEdit: {
            type: Boolean,
            default: false,
        },
        resizeToFit: {
            type: Boolean,
            default: true,
        },
    },
    emits: [
        'grid-ready',
        'cell-values-changed',
        'cell-key-down',
        'cell-value-changed',
        'cell-mouse-over',
        'row-data-updated',
        'model-updated',
    ],
    data() {
        return {
            gridApi: null,
        };
    },
    computed: {
        defaultColDefExtended() {
            return markRaw(
                merge({}, toRaw(this.defaultColDef) || {}, {
                    suppressHeaderMenuButton: true,
                    suppressKeyboardEvent: params => {
                        // return true to suppress ag grid next cell focus behaviour if user hit end/home/left arrow/right arrow keys
                        const { keyCode } = params.event;

                        return keyCode === 35 || keyCode === 36 || keyCode === 37 || keyCode === 39;
                    },
                })
            );
        },
        gridOptionsExtended() {
            return markRaw(
                merge({}, toRaw(this.gridOptions) || {}, {
                    // ag-grid suppressCellFocus: true grid option is not working,
                    // need to override navigation methods to prevent focusing next cell when user presses arrow keys
                    // in order for navigation to work inside inputs
                    navigateToNextCell: () => {
                        return null;
                    },
                    // there is an input inside rateCards header, so need to override navigateToNextHeader method as well
                    navigateToNextHeader: () => {
                        return null;
                    },
                })
            );
        },
    },
    methods: {
        onReady(params) {
            this.gridApi = markRaw(params.api);
            if (this.resizeToFit) this.gridApi.sizeColumnsToFit();
            this.$emit('grid-ready', params);
        },
        onCellValueChanged(event) {
            this.$emit('cell-value-changed', event);
        },
        onCellKeyDown(event) {
            this.$emit('cell-key-down', event);
        },
        debouncedOnCellMouseOver: debounce(function($event) {
            this.onCellMouseOver($event);
        }, 100),
        onCellMouseOver(event) {
            this.$emit('cell-mouse-over', event);
        },
        onRowDataUpdated(event) {
            this.$emit('row-data-updated', event);
            if (this.resizeToFit) {
                this.$nextTick(() => {
                    this.gridApi.sizeColumnsToFit();
                });
            }
        },
        onModelUpdated(event) {
            this.$emit('model-updated', event);
        },
    },
};
</script>

<style scoped lang="scss">
:deep(.ag-root-wrapper) {
    min-height: 40rem;
}
:deep(.ag-header-cell-resize::after) {
    content: '';
    position: absolute;
    z-index: 1;
    display: block;
    left: calc(50% - 1px);
    width: 1px;
    height: 30%;
    top: calc(50% - 15%);
    background-color: var(--ag-header-column-resize-handle-color, rgba(186, 191, 199, 0.5));
}

:deep(.ag-cell-wrapper) {
    height: 100%;
}
</style>
